// src/pages/masters/CustomerMasterPage.js
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, Badge, PageHeader } from '../../components/ui';
import { Plus, UserPlus } from 'lucide-react';

const initialForm = { customer_code: '', customer_name: '', pan_number: '', demat_account: '', broker_id: '', phone: '', email: '', address: '', opening_balance: 0 };

export default function CustomerMasterPage() {
  const [customers, setCustomers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get('/masters/brokerages').then(r => setBrokers(r.data));
    loadCustomers();
  }, []);

  const loadCustomers = () => api.get('/masters/customers').then(r => setCustomers(r.data));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) { await api.put(`/masters/customers/${editId}`, form); toast.success('Customer updated!'); }
      else { await api.post('/masters/customers', form); toast.success('Customer added!'); }
      setForm(initialForm); setShowForm(false); setEditId(null);
      loadCustomers();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const startEdit = (c) => {
    setForm({ customer_code: c.customer_code, customer_name: c.customer_name, pan_number: c.pan_number || '', demat_account: c.demat_account || '', broker_id: c.broker_id || '', phone: c.phone || '', email: c.email || '', address: c.address || '', opening_balance: c.opening_balance || 0 });
    setEditId(c.id); setShowForm(true);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Customer Master"
        subtitle="Manage investor accounts"
        action={<Btn onClick={() => { setShowForm(!showForm); setEditId(null); setForm(initialForm); }}><UserPlus size={16} /> Add Customer</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title={editId ? 'Edit Customer' : 'Add New Customer'} />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <Input label="Customer Code *" value={form.customer_code} onChange={set('customer_code')} placeholder="e.g. CUST001" disabled={!!editId} />
              <Input label="Customer Name *" value={form.customer_name} onChange={set('customer_name')} placeholder="Full Name" />
              <Input label="PAN Number" value={form.pan_number} onChange={set('pan_number')} placeholder="e.g. ABCDE1234F" maxLength={10} />
              <Input label="Demat Account" value={form.demat_account} onChange={set('demat_account')} placeholder="Demat/BO ID" />
              <Select label="Broker" value={form.broker_id} onChange={set('broker_id')}>
                <option value="">Select Broker</option>
                {brokers.map(b => <option key={b.id} value={b.id}>{b.broker_name}</option>)}
              </Select>
              <Input label="Phone" value={form.phone} onChange={set('phone')} placeholder="Mobile number" />
              <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="Email address" />
              <Input label="Opening Balance (₹)" type="number" value={form.opening_balance} onChange={set('opening_balance')} />
              <Input label="Address" value={form.address} onChange={set('address')} placeholder="Address" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" disabled={loading}>{loading ? 'Saving...' : editId ? 'Update' : 'Add Customer'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title="All Customers" subtitle={`${customers.length} customers`} />
        <Table
          headers={['Code', 'Name', 'PAN', 'Demat Account', 'Broker', 'Phone', 'Email', 'Actions']}
          rows={customers.map(c => [
            <strong style={{ fontFamily: 'monospace' }}>{c.customer_code}</strong>,
            c.customer_name,
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.pan_number || '—'}</span>,
            <span style={{ fontSize: 12, color: '#64748b' }}>{c.demat_account || '—'}</span>,
            c.broker_name ? <Badge color="purple">{c.broker_name}</Badge> : '—',
            c.phone || '—',
            c.email || '—',
            <Btn size="sm" variant="ghost" onClick={() => startEdit(c)}>Edit</Btn>
          ])}
          emptyMessage="No customers yet. Add your first customer."
        />
      </Card>
    </div>
  );
}
